/**
 * Layout B: Farm Dashboard Layout
 * Left dark-teal sidebar + top bar + content area.
 * Used for all farm-scoped pages.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Drawer,
  IconButton,
  CssBaseline,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Menu as MenuIcon} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { fetchFarms, selectFarms } from '@/store/slices/farmSlice';
import TopBarActions from './TopBarActions';
import FarmSelectorDropdown from './FarmSelectorDropdown';
import SidebarNav from './SidebarNav';
import { useLayoutServices } from '@/hooks/useLayoutServices';
import { useWebSocketFarmSubscription } from '@/hooks/useWebSocketFarmSubscription';
import { tokens } from '@/theme/tokens';

const SIDEBAR_WIDTH = tokens.layout.sidebarWidth;

const FarmDashboardLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const dispatch = useAppDispatch();
  const farms = useAppSelector(selectFarms);
  const { farmId } = useParams<{ farmId: string }>();

  useLayoutServices();
  useWebSocketFarmSubscription();

  // Ensure farms are loaded for the dropdown
  useEffect(() => {
    if (farms.length === 0) {
      dispatch(fetchFarms());
    }
  }, [dispatch, farms.length]);

  const handleDrawerClose = () => setMobileOpen(false);
  const sidebarContent = <SidebarNav onClose={isMobile ? handleDrawerClose : undefined} />;

  // width + minWidth keep the root flex item (child of the App-level flex
  // wrapper) from inflating to its content's min-width on small screens.
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%', minWidth: 0 }}>
      <CssBaseline />

      {/* Sidebar */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerClose}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: '100vw',
              boxSizing: 'border-box',
              bgcolor: 'transparent',
              borderRight: 'none',
              p: 1,
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: SIDEBAR_WIDTH + 16,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: SIDEBAR_WIDTH + 16,
              boxSizing: 'border-box',
              bgcolor: 'transparent',
              borderRight: 'none',
              p: 1,
            },
          }}
          open
        >
          {sidebarContent}
        </Drawer>
      )}

      {/* Main content area */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Top bar */}
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            width: { md: `calc(100% - ${SIDEBAR_WIDTH + 16}px)` },
            ml: { md: `${SIDEBAR_WIDTH + 16}px` },
            bgcolor: 'background.paper',
            color: 'text.primary',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Toolbar>
            {isMobile && (
              <IconButton
                color="inherit"
                edge="start"
                onClick={() => setMobileOpen(!mobileOpen)}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
              <FarmSelectorDropdown />
            </Box>

            <TopBarActions />
          </Toolbar>
        </AppBar>

        {/* Page content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 1, sm: 2, md: 3 },
            pt: {
              xs: 'calc(56px + 8px)',
              sm: 'calc(64px + 16px)',
              md: 'calc(64px + 24px)',
            },
            minWidth: 0,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default FarmDashboardLayout;
